/**
 * JOB BOARD
 *
 * Clean, production-quality available jobs screen.
 * Single category filter row. Green accept button + green payout.
 * Locked jobs visible but gated behind training certification.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  View, Text, FlatList, StyleSheet, TouchableOpacity,
  Modal, ActivityIndicator, RefreshControl, Platform, Animated,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { colors, spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { ensureLocationEnabled } from '../../services/location';
import { connectSocket } from '../../services/socket';
import { MOCK_CERTIFICATIONS, MOCK_COURSES } from '../../data/mockData';
import { wooService } from '../../services/woocommerce';
import { RootState, addActiveJob, removeJob } from '../../store';
import { mapToTrainingCategory, getCourseIdForCategory, isDriverCertifiedForJob, getTrainingCategoryLabel, needsPalletizingCert } from '../../utils/categoryMapping';
import { pairPickupWithDelivery } from '../../utils/pickupWindow';
import { bufferedPickup, bufferedPickupWindowList } from '../../utils/windows';
import * as Location from 'expo-location';

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const REJECT_REASONS = [
  { id: 'TOO_FAR', label: 'Too far away' },
  { id: 'PRICE_TOO_LOW', label: 'Payout too low' },
  { id: 'NOT_AVAILABLE', label: 'Not available' },
  { id: 'VEHICLE_MISMATCH', label: 'Wrong vehicle type' },
  { id: 'OTHER', label: 'Other reason' },
];

// Derive a short "City, ST" label from a full address string (never show full street address).
function shortLocation(addr?: string): string {
  if (!addr) return '';
  const parts = addr.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  // Typical "Street, City, ST ZIP" → take City + state abbreviation.
  if (parts.length >= 3) {
    const city = parts[parts.length - 2];
    const stateZip = parts[parts.length - 1].replace(/\d{5}(-\d{4})?/, '').trim();
    const st = (stateZip.match(/[A-Z]{2}\b/) || [stateZip])[0];
    return st ? `${city}, ${st}` : city;
  }
  if (parts.length === 2) return parts[0];
  return parts[0];
}

// Extract the 5-digit ZIP from a full address string.
function zipOf(addr?: string): string {
  if (!addr) return '';
  const m = addr.match(/\b(\d{5})(?:-\d{4})?\b/);
  return m ? m[1] : '';
}

// Build a "Pickup City, ST ZIP → Delivery City, ST ZIP" route line (ZIP always shown).
function getRouteLabel(job: any): string {
  const fromZip = zipOf(job.pickupAddress);
  const toZip = zipOf(job.deliveryAddress);
  const from = (shortLocation(job.pickupAddress) || 'Pickup pending') + (fromZip ? ` ${fromZip}` : '');
  const to = (shortLocation(job.deliveryAddress) || 'Delivery pending') + (toZip ? ` ${toZip}` : '');
  return `${from} → ${to}`;
}

function getJobDisplayTitle(job: any): string {
  if (job.displayTitle) return job.displayTitle;
  // The human order number only. wooOrderId is "V2-<cuid>" (or "STS-V2-<cuid>" on a storage
  // pickup, which has no order number at all), which nobody can read or quote, so a job
  // with no number shows its title alone.
  const orderNum = job.orderNumber || null;
  // Use shortened product title if available, otherwise fall back to category label
  if (job.productTitle) {
    // Shorten long titles: take up to first delimiter (–, -, |, comma after brand)
    const short = job.productTitle
      .replace(/\s*[-–|]\s*(Like New|Used|New|Good|Excellent|Fair|Great|Open Box).*$/i, '')
      .replace(/,\s*([\d]+[WHD"']|[\d]+\s*(in|inch|cm|mm)).*$/i, '')
      .trim();
    return orderNum ? `#${orderNum} – ${short}` : short;
  }
  const label = getTrainingCategoryLabel(job.categorySlug || 'general');
  return orderNum ? `Order #${orderNum} – ${label}` : label;
}

export function JobBoardScreen({ navigation }: any) {
  const dispatch = useDispatch();
  const [jobs, setJobs]             = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected]     = useState<any>(null);
  const [accepting, setAccepting]   = useState(false);
  const [rejectJob, setRejectJob]   = useState<any>(null);

  // Fleet manager state — server-authoritative
  const driver = useSelector((s: RootState) => s.auth.driver);
  const [isFleetManager, setIsFleetManager] = useState(driver?.isFleetManager || false);
  const isFleetMember = !!(driver as any)?.fleetManagerId && !isFleetManager;
  const [fleetMembers, setFleetMembers] = useState<any[]>([]);
  const [fleetAssignJob, setFleetAssignJob] = useState<any>(null);
  const [assigningFleet, setAssigningFleet] = useState(false);

  // Track which jobs the driver has viewed (persisted)
  const [viewedJobIds, setViewedJobIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    AsyncStorage.getItem('viewed_job_ids').then(data => {
      if (data) { try { setViewedJobIds(new Set(JSON.parse(data))); } catch {} }
    }).catch(() => {});
  }, []);
  const markJobViewed = useCallback((jobId: string) => {
    setViewedJobIds(prev => {
      if (prev.has(jobId)) return prev;
      const next = new Set(prev);
      next.add(jobId);
      AsyncStorage.setItem('viewed_job_ids', JSON.stringify([...next])).catch(() => {});
      return next;
    });
  }, []);

  // Driver's live location for distance calculation + permission gating
  const [driverLocation, setDriverLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationRequested, setLocationRequested] = useState(false);

  const requestLocation = useCallback(() => {
    setLocationLoading(true);
    setLocationDenied(false);
    setLocationRequested(true);
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.geolocation) {
      const onOk = (pos: any) => {
        setDriverLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setLocationLoading(false);
      };
      // Only code 1 (PERMISSION_DENIED) means blocked. Timeout/unavailable (2/3) is a
      // slow/indoor fix — retry once leniently and don't flag it as denied.
      const onErr = (err: any) => {
        console.warn('[Location] Web geolocation error:', err?.code, err?.message);
        if (err && err.code === 1) { setLocationDenied(true); setLocationLoading(false); return; }
        navigator.geolocation.getCurrentPosition(
          onOk,
          (e2: any) => { if (e2 && e2.code === 1) setLocationDenied(true); setLocationLoading(false); },
          { enableHighAccuracy: false, timeout: 30000, maximumAge: 600000 },
        );
      };
      navigator.geolocation.getCurrentPosition(onOk, onErr, { enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 });
    } else {
      (async () => {
        try {
          const { status } = await Location.requestForegroundPermissionsAsync();
          if (status === 'granted') {
            const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
            setDriverLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          } else {
            setLocationDenied(true);
          }
        } catch {
          setLocationDenied(true);
        }
        setLocationLoading(false);
      })();
    }
  }, []);

  // Check if permission is already granted (without triggering prompt)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
        if (result.state === 'granted') {
          // Already granted — fetch silently
          requestLocation();
        }
        // If 'prompt' or 'denied', wait for user gesture
      }).catch(() => {
        // permissions API not available — wait for user gesture
      });
    } else if (Platform.OS !== 'web') {
      // Native: expo-location handles permissions fine on mount
      requestLocation();
    }
  }, [requestLocation]);

  // Smart job filters (match Claude design): All · Ready to drive · Top pay · Nearby · Solo
  type JobFilter = 'All' | 'Ready to drive' | 'Top pay' | 'Nearby' | 'Solo';
  const [filter, setFilter] = useState<JobFilter>('All');

  const certifications = useSelector((s: RootState) => s.auth.certifications) ?? MOCK_CERTIFICATIONS;
  const activeJobIds = useSelector((s: RootState) => s.jobs.activeJobs.map((j: any) => j.id));

  const isCertified = useCallback((job: any) => {
    return isDriverCertifiedForJob(job.categorySlug || 'general', certifications) && !(needsPalletizingCert(job) && !certifications.includes('palletizing'));
  }, [certifications]);

  // Unique categories
  const jobCategories = useMemo(() => {
    const counts = new Map<string, number>();
    jobs.forEach(j => {
      const cat = j.categorySlug || 'general';
      counts.set(cat, (counts.get(cat) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([cat, count]) => ({ slug: cat, label: getTrainingCategoryLabel(cat), count }));
  }, [jobs]);

  // Apply filters
  const filteredJobs = useMemo(() => {
    let result = activeJobIds.length > 0 ? jobs.filter(j => !activeJobIds.includes(j.id)) : jobs;
    // Hide completed/cancelled jobs. For regular drivers, also hide already-accepted jobs.
    // Fleet members see pre-assigned jobs (which have driverId set to their fleet manager), so skip that filter.
    result = result.filter(j => j.status !== 'COMPLETED' && j.status !== 'CANCELLED');
    if (!isFleetMember) {
      result = result.filter(j => !j.driverId);
    }
    // Distance for the "Nearby" sort: real haversine from the driver's location
    // when available, else the backend-provided _distanceMiles.
    const distOf = (j: any) => {
      if (driverLocation && j.pickupLat && j.pickupLng) {
        const toR = (d: number) => (d * Math.PI) / 180;
        const dLat = toR(j.pickupLat - driverLocation.latitude);
        const dLng = toR(j.pickupLng - driverLocation.longitude);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toR(driverLocation.latitude)) * Math.cos(toR(j.pickupLat)) * Math.sin(dLng / 2) ** 2;
        return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      }
      return j._distanceMiles != null ? j._distanceMiles : Infinity;
    };
    if (filter === 'Ready to drive') result = result.filter(j => isCertified(j));
    else if (filter === 'Solo') result = result.filter(j => (j.helpersNeeded || 1) <= 1);
    else if (filter === 'Top pay') result = [...result].sort((a, b) => (b.payoutAmount || 0) - (a.payoutAmount || 0));
    else if (filter === 'Nearby') result = [...result].sort((a, b) => distOf(a) - distOf(b));
    return result;
  }, [jobs, filter, activeJobIds, isCertified, isFleetMember, driverLocation]);

  const navigateToTraining = useCallback((job: any) => {
    const hasDriverCert = certifications.includes('driver-certification');
    const trainingCat = mapToTrainingCategory(job.categorySlug || job.productCategory || 'general');
    const courseId = getCourseIdForCategory(trainingCat);

    if (!hasDriverCert) {
      // Go to driver cert quiz first, then chain to category quiz
      navigation.navigate('Quiz', {
        courseId: 'course-driver-cert',
        title: 'General Driver Certification',
        returnToCourse: courseId || undefined,
        fromJobId: job.id,
        fromJob: job,
      });
      return;
    }
    // Already have driver cert — go directly to category quiz
    if (courseId) {
      const course = MOCK_COURSES.find(c => c.id === courseId);
      navigation.navigate('Quiz', { courseId, title: course?.title || 'Training', fromJobId: job.id, fromJob: job });
    } else {
      navigation.navigate('Main', { screen: 'Training' });
    }
  }, [navigation, certifications]);

  // Monotonic request id: the board has FOUR concurrent triggers (20s poll, two socket
  // events, manual refresh) and getAvailableJobs latency varies (it runs a woo sync first),
  // so a slow earlier call could resolve AFTER a fresh later one and clobber the list
  // (a just-posted job vanishes / an accepted one reappears). Only the latest call may write.
  const loadReqRef = useRef(0);
  const loadJobs = useCallback(async (isRefresh = false) => {
    const myReq = ++loadReqRef.current;
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const result = await wooService.getAvailableJobs();
      if (myReq === loadReqRef.current) setJobs(result || []);
    } catch { /* keep existing */ }
    finally {
      if (myReq === loadReqRef.current) {
        if (isRefresh) setRefreshing(false); else setLoading(false);
      }
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Auto-refresh the board every 20s so new jobs always appear without tapping
  // anything (the socket below also pushes updates in real time).
  useEffect(() => {
    const id = setInterval(() => loadJobs(true), 20000);
    return () => clearInterval(id);
  }, [loadJobs]);

  // Real-time job board updates via Socket.io
  useEffect(() => {
    try {
      const s = connectSocket();

      s.on('job:accepted', (data: { jobId: string }) => {
        if (data?.jobId) {
          setJobs(prev => prev.filter(j => j.id !== data.jobId));
        }
      });

      s.on('job:cancelled', () => {
        loadJobs(true);
      });

      // New job posted to the board — reload silently so it just appears.
      s.on('job:available', () => {
        loadJobs(true);
      });

      return () => {
        try {
          s.off('job:accepted');
          s.off('job:cancelled');
          s.off('job:available');
        } catch { /* ignore cleanup errors */ }
      };
    } catch {
      // Socket unavailable — fall back to manual refresh only
      return undefined;
    }
  }, [loadJobs]);

  const handleAcceptPress = useCallback((job: any) => {
    if (!driverLocation) {
      if (locationDenied) {
        alert('Location access is required to accept jobs. Please enable location in your browser settings and refresh the page.');
      } else {
        // Trigger location request from user gesture (works on iOS Safari)
        requestLocation();
        alert('Please allow location access to accept jobs. Tap "Allow" when prompted.');
      }
      return;
    }
    setSelected(job);
  }, [driverLocation, locationDenied, requestLocation]);

  // Server-authoritative fleet check — fetch fleet members directly
  useEffect(() => {
    api.get('/drivers/me/fleet-members')
      .then(r => {
        if (Array.isArray(r.data)) {
          setIsFleetManager(true);
          setFleetMembers(r.data);
        }
      })
      .catch(() => setIsFleetManager(false));
  }, []);

  const handleAccept = async (job: any, windowId: string) => {
    if (accepting) return; // guard a double-tap → duplicate POST + double ActiveJob push
    // Confirm to prevent accidental accepts. Web confirm — drivers are on the PWA.
    const itemName = String(job?.productTitle || 'this job').replace(/&amp;/g, '&').replace(/\s*[-–]\s*[A-Za-z .'-]+,\s*[A-Z]{2}\s*$/, '');
    if (typeof window !== 'undefined' && window.confirm && !window.confirm(`Accept "${itemName}"?\n\nOnly accept if you can complete both the pickup and the delivery.`)) return;
    setAccepting(true);
    // HARD GATE: a driver must have location enabled to accept a job. This prompts +
    // posts a fresh fix (marking them locationGranted on the backend) so live tracking
    // works the moment they head out. No location → no accept.
    const locOk = await ensureLocationEnabled();
    if (!locOk) {
      setAccepting(false);
      alert('Please enable location to accept jobs. Commonplace needs your location for live delivery tracking. Turn it on in your browser/app settings and try again.');
      return;
    }
    const selectedWindow = job.deliveryWindows?.find((w: any) => w.id === windowId);
    // Backend auto-schedules when windowId is provided, so set status to SCHEDULED
    const acceptedJob = {
      ...job,
      status: 'SCHEDULED',
      selectedWindow,
      scheduledWindowStart: selectedWindow?.startTime,
      scheduledWindowEnd: selectedWindow?.endTime,
    };
    try {
      await api.post(`/jobs/${job.id}/accept`, { windowId });
    } catch (e: any) {
      // Server rejected the accept (already taken, training required, not approved).
      // Do NOT add a phantom active job — surface the message and refresh the board.
      setAccepting(false);
      setSelected(null);
      alert(e?.response?.data?.error || 'Could not accept job');
      loadJobs(true);
      return;
    }
    dispatch(addActiveJob(acceptedJob));
    dispatch(removeJob(job.id));
    setSelected(null);
    setJobs(prev => prev.filter(j => j.id !== job.id));
    setAccepting(false);

    // Fleet manager: show assignment modal instead of navigating directly
    if (isFleetManager && fleetMembers.length > 0) {
      setFleetAssignJob(acceptedJob);
    } else {
      navigation.navigate('ActiveJob', { jobId: job.id, job: acceptedJob });
    }
  };

  const handleFleetAssign = async (fleetDriverId: string) => {
    if (!fleetAssignJob || assigningFleet) return; // guard double-tap
    setAssigningFleet(true);
    try {
      await api.post(`/jobs/${fleetAssignJob.id}/assign-fleet`, { fleetDriverId });
    } catch (e: any) {
      // Don't fake success — a failed assignment must surface, not look assigned.
      setAssigningFleet(false);
      alert(e?.response?.data?.error || 'Could not assign driver. Please try again.');
      return;
    }
    setAssigningFleet(false);
    const assignedJob = { ...fleetAssignJob, assignedFleetDriverId: fleetDriverId };
    dispatch(addActiveJob(assignedJob));
    setFleetAssignJob(null);
    if (fleetDriverId === 'self') {
      navigation.navigate('ActiveJob', { jobId: fleetAssignJob.id, job: assignedJob });
    }
    // If assigned to someone else, stay on job board
  };

  const handleReject = async (jobId: string, reason: string) => {
    try { await api.post(`/jobs/${jobId}/reject`, { reason }); } catch {}
    setRejectJob(null);
    setJobs(prev => prev.filter(j => j.id !== jobId));
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.headerTitle}>{isFleetMember ? 'Assigned Jobs' : 'Available Jobs'}</Text>
          <Text style={styles.headerSub}>
            {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'} near you
          </Text>
        </View>
        <View style={styles.countBubble}>
          <Text style={styles.countText}>{filteredJobs.length}</Text>
        </View>
      </View>

      {/* Tap to refresh — jobs ALSO auto-appear (socket + 20s poll); this is just a manual nudge */}
      {Platform.OS === 'web' && !refreshing && (
        <TouchableOpacity style={styles.refreshRow} onPress={() => loadJobs(true)} activeOpacity={0.7}>
          <Ionicons name="refresh-outline" size={14} color={RD.textMuted} />
          <Text style={styles.refreshText}>Tap to refresh</Text>
        </TouchableOpacity>
      )}
      {refreshing && (
        <View style={styles.refreshRow}>
          <ActivityIndicator size="small" color={RD.ink} />
          <Text style={styles.refreshText}>Refreshing...</Text>
        </View>
      )}

      {/* Smart filters — All · Ready to drive · Top pay · Nearby · Solo (match Claude design) */}
      <View style={styles.filterRow}>
        {(['All', 'Ready to drive', 'Top pay', 'Nearby', 'Solo'] as JobFilter[]).map(f => {
          const icon = f === 'Ready to drive' ? 'checkmark-circle' : f === 'Top pay' ? 'cash-outline' : f === 'Nearby' ? 'location-outline' : f === 'Solo' ? 'person-outline' : 'list';
          return (
            <TouchableOpacity key={f} style={[styles.pill, filter === f && styles.pillActive]} onPress={() => setFilter(f)}>
              <Ionicons name={icon as any} size={13} color={filter === f ? '#fff' : RD.textMuted} style={{ marginRight: 5 }} />
              <Text style={[styles.pillText, filter === f && styles.pillTextActive]}>{f}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Location permission banner — show when not yet requested or denied */}
      {!driverLocation && !locationLoading && (
        <TouchableOpacity
          style={{
            marginHorizontal: spacing.lg,
            marginBottom: 10,
            padding: 14,
            backgroundColor: locationDenied ? RD.dangerSoft : RD.infoSoft,
            borderRadius: 18,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            borderWidth: 1,
            borderColor: locationDenied ? RD.danger + '40' : RD.info + '40',
          }}
          onPress={requestLocation}
          activeOpacity={0.7}
        >
          <View style={{
            width: 40, height: 40, borderRadius: 20,
            backgroundColor: locationDenied ? RD.dangerSoft : RD.infoSoft,
            justifyContent: 'center', alignItems: 'center',
          }}>
            <Ionicons name="location" size={22} color={locationDenied ? RD.danger : RD.info} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: locationDenied ? RD.danger : RD.info }}>
              {locationDenied ? 'Location Access Denied' : 'Enable Location'}
            </Text>
            <Text style={{ fontSize: 12, color: locationDenied ? RD.danger : RD.info, marginTop: 2 }}>
              {locationDenied
                ? 'Open browser settings to allow location access, then tap here.'
                : 'Tap here to enable location and see how far each job is from you.'}
            </Text>
          </View>
          <View style={{
            paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999,
            backgroundColor: locationDenied ? RD.danger : RD.info,
          }}>
            <Text style={{ fontSize: 12, fontWeight: '700', color: '#fff' }}>
              {locationDenied ? 'Retry' : 'Allow'}
            </Text>
          </View>
        </TouchableOpacity>
      )}

      {/* Job List */}
      <FlatList
        data={filteredJobs}
        keyExtractor={item => item.id}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadJobs(true)} tintColor={RD.ink} />}
        ListEmptyComponent={!loading ? (
          <View style={styles.emptyState}>
            <Ionicons name="briefcase-outline" size={48} color={RD.g30} />
            <Text style={styles.emptyTitle}>No Jobs Available</Text>
            <Text style={styles.emptyText}>Jobs will appear when new orders come in.</Text>
          </View>
        ) : null}
        renderItem={({ item }) => {
          const locked = !isCertified(item);
          return (
            <JobCard
              job={{ ...item, hidePayouts: isFleetMember, isFleetMember }}
              locked={isFleetMember ? false : locked}
              driverLocation={driverLocation}
              isNew={!viewedJobIds.has(item.id)}
              onAccept={() => {
                markJobViewed(item.id);
                if (isFleetMember) {
                  dispatch(addActiveJob(item));
                  navigation.navigate('ActiveJob', { jobId: item.id, job: item });
                } else {
                  handleAcceptPress(item);
                }
              }}
              onReject={isFleetMember ? undefined : () => setRejectJob(item)}
              onDetail={() => { markJobViewed(item.id); navigation.navigate(isFleetMember ? 'ActiveJob' : 'JobDetail', { jobId: item.id, job: item }); }}
              onGoTraining={() => handleAcceptPress(item)}
            />
          );
        }}
        contentContainerStyle={styles.list}
      />

      <AcceptModal
        job={selected}
        visible={!!selected}
        locked={selected ? !isCertified(selected) : false}
        onClose={() => setSelected(null)}
        onAccept={handleAccept}
        onGoTraining={() => { setSelected(null); if (selected) navigateToTraining(selected); }}
        loading={accepting}
      />
      <RejectModal
        job={rejectJob}
        visible={!!rejectJob}
        onClose={() => setRejectJob(null)}
        onReject={handleReject}
      />

      {/* Fleet Assignment Modal */}
      <Modal visible={!!fleetAssignJob} transparent animationType="slide" onRequestClose={() => setFleetAssignJob(null)}>
        <View style={{ flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, paddingBottom: 40, maxHeight: '70%' }}>
            <Text style={{ fontSize: 20, fontWeight: '800', color: colors.gray85, marginBottom: 4 }}>Assign Driver</Text>
            <Text style={{ fontSize: 14, color: colors.gray50, marginBottom: 16 }}>
              {fleetAssignJob?.productTitle || 'Job'}
            </Text>
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', padding: 14, backgroundColor: '#f0faf2', borderRadius: 10, marginBottom: 8, borderWidth: 1, borderColor: '#009B1C20' }}
              onPress={() => handleFleetAssign('self')}
              disabled={assigningFleet}
            >
              <Ionicons name="person" size={20} color="#009B1C" />
              <Text style={{ marginLeft: 10, fontSize: 15, fontWeight: '700', color: '#009B1C' }}>Myself</Text>
            </TouchableOpacity>
            {fleetMembers.map((m: any) => (
              <TouchableOpacity
                key={m.id}
                style={{ flexDirection: 'row', alignItems: 'center', padding: 14, backgroundColor: colors.gray05, borderRadius: 10, marginBottom: 8, borderWidth: 1, borderColor: '#eee' }}
                onPress={() => handleFleetAssign(m.id)}
                disabled={assigningFleet}
              >
                <Ionicons name="people" size={20} color={colors.gray60} />
                <View style={{ marginLeft: 10, flex: 1 }}>
                  <Text style={{ fontSize: 15, fontWeight: '600', color: colors.gray85 }}>{m.name}</Text>
                  <Text style={{ fontSize: 12, color: colors.gray50 }}>{(m.vehicleType || '').replace(/_/g, ' ')}</Text>
                </View>
              </TouchableOpacity>
            ))}
            {assigningFleet && <ActivityIndicator size="small" color={colors.gray60} style={{ marginTop: 8 }} />}
            <TouchableOpacity onPress={() => setFleetAssignJob(null)} style={{ alignSelf: 'center', padding: 12, marginTop: 8 }}>
              <Text style={{ color: colors.gray50, fontSize: 14 }}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

/* ── Job Card ── */
function FlashingBulb() {
  const opacity = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.2, duration: 600, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [opacity]);
  return (
    <Animated.View style={{ opacity, position: 'absolute', top: 12, right: 12, zIndex: 5, width: 14, height: 14, borderRadius: 7, backgroundColor: '#2196F3', shadowColor: '#2196F3', shadowOpacity: 0.5, shadowRadius: 6, shadowOffset: { width: 0, height: 0 }, elevation: 6 }} />
  );
}

function JobCard({ job, locked, driverLocation, isNew, onAccept, onReject, onDetail, onGoTraining }: any) {
  const title = getJobDisplayTitle(job);
  const categoryLabel = getTrainingCategoryLabel(job.categorySlug || 'general');
  const route = getRouteLabel(job);
  const payout = job.hidePayouts ? '' : (job.payoutAmount != null ? `$${Math.round(job.payoutAmount)}` : '—');
  const helpers = job.helpersNeeded || 1;
  const crewLabel = helpers === 1 ? 'Solo' : `${helpers} crew`;
  const handleAcceptPress = locked ? onGoTraining : onAccept;

  // Distance from driver's current location to pickup
  const distFromDriver = driverLocation && job.pickupLat && job.pickupLng
    ? haversineDistance(driverLocation.latitude, driverLocation.longitude, job.pickupLat, job.pickupLng)
    : null;
  const distLabel = distFromDriver != null
    ? (distFromDriver < 1 ? `${(distFromDriver * 5280).toFixed(0)} ft` : distFromDriver < 10 ? `${distFromDriver.toFixed(1)} mi` : `${Math.round(distFromDriver)} mi`)
    : (job._distanceMiles != null && job._distanceMiles > 0 ? `${job._distanceMiles.toFixed(1)} mi` : null);

  return (
    <TouchableOpacity style={styles.card} onPress={onDetail} activeOpacity={0.8}>
      {isNew && <FlashingBulb />}

      {/* Title + route on the left, big payout on the right */}
      <View style={styles.cardTop}>
        <View style={styles.cardTopLeft}>
          <Text style={styles.orderTitle} numberOfLines={2}>{title}</Text>
          <Text style={styles.routeText} numberOfLines={1}>{route}</Text>

          {/* Soft pills row */}
          <View style={styles.pillsRow}>
            {locked && (
              <View style={[styles.softPill, styles.softPillAmber]}>
                <Ionicons name="lock-closed" size={12} color={RD.amber} />
                <Text style={[styles.softPillText, { color: RD.amber }]}>{categoryLabel} training required</Text>
              </View>
            )}
            {job.pickupWindows?.length > 0 && (
              <View style={[styles.softPill, styles.softPillMaroon]}>
                <Ionicons name="time-outline" size={12} color={RD.maroon} />
                <Text style={[styles.softPillText, { color: RD.maroon }]}>
                  {job.pickupWindows.length} {job.pickupWindows.length === 1 ? 'window' : 'windows'}
                </Text>
              </View>
            )}
            <View style={styles.softPill}>
              <Ionicons name="people-outline" size={13} color={RD.textMuted} />
              <Text style={styles.softPillText}>{crewLabel}</Text>
            </View>
            {distLabel && (
              <View style={styles.softPill}>
                <Ionicons name="location-outline" size={13} color={RD.textMuted} />
                <Text style={styles.softPillText}>{distLabel}</Text>
              </View>
            )}
          </View>
        </View>

        {!job.hidePayouts && (
          <View style={styles.payoutBlock}>
            <Text style={styles.payout}>{payout}</Text>
            <Text style={styles.payoutLabel}>payout</Text>
          </View>
        )}
      </View>

      {/* Available pickup windows — visible BEFORE accepting so drivers can compare
          availability. These are the buffered (driver-facing) times. */}
      {(() => {
        const wins = bufferedPickupWindowList(job);
        if (!wins.length) return null;
        return (
          <View style={styles.windowsPreview}>
            <Text style={styles.windowsPreviewLabel}>
              {job.isTwoDay ? 'Available windows — pickup, then delivery the next day' : 'Available pickup windows'}
            </Text>
            {wins.slice(0, 3).map(w => (
              <View key={w.id} style={styles.windowsPreviewRow}>
                <Ionicons name="time-outline" size={13} color={RD.maroon} />
                <Text style={styles.windowsPreviewText}>
                  {w.day} · {w.time}
                  {w.deliveryDay ? `  →  deliver ${w.deliveryDay} · ${w.deliveryTime}` : ''}
                </Text>
              </View>
            ))}
            {wins.length > 3 && (
              <Text style={styles.windowsPreviewMore}>+{wins.length - 3} more — tap to view</Text>
            )}
          </View>
        );
      })()}

      {/* Actions */}
      <View style={styles.actionRow}>
        {!job.isFleetMember && (
          <TouchableOpacity style={styles.passBtn} onPress={onReject} activeOpacity={0.7}>
            <Text style={styles.passText}>Pass</Text>
          </TouchableOpacity>
        )}
        {locked ? (
          <TouchableOpacity style={[styles.lockedBtn, job.isFleetMember && { flex: 1 }]} onPress={handleAcceptPress} activeOpacity={0.8}>
            <Ionicons name="lock-closed" size={17} color={RD.textSec} />
            <Text style={styles.lockedBtnText}>Training Required</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={[styles.acceptBtn, job.isFleetMember && { flex: 1 }]} onPress={handleAcceptPress} activeOpacity={0.8}>
            <Text style={styles.acceptText}>{job.isFleetMember ? 'Start Job' : 'Accept Job'}</Text>
            <Ionicons name={job.isFleetMember ? 'arrow-forward-circle' : 'checkmark-circle'} size={17} color={colors.white} />
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );
}

/* ── Accept Modal ── */
function AcceptModal({ job, visible, locked, onClose, onAccept, onGoTraining, loading }: any) {
  const [selectedWindow, setSelectedWindow] = useState<string | null>(null);
  if (!job) return null;

  const title = getJobDisplayTitle(job);
  const categoryLabel = getTrainingCategoryLabel(job.categorySlug || 'general');

  if (locked) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
        <View style={styles.modalOverlay}>
          <TouchableOpacity style={styles.modalBackdrop} onPress={onClose} activeOpacity={1} />
          <View style={styles.modalSheet}>
            <View style={styles.modalHandle} />
            <View style={styles.lockedContent}>
              <View style={styles.lockedIcon}><Ionicons name="lock-closed" size={30} color={colors.gray70} /></View>
              <Text style={styles.modalTitle}>Certification Required</Text>
              <Text style={styles.lockedDesc}>
                Complete the <Text style={{ fontWeight: '700' }}>{categoryLabel}</Text> training course before accepting this job.
              </Text>
              <Text style={styles.lockedPayout}>${Math.round(job.payoutAmount || 0)} payout after certification</Text>
            </View>
            <View style={styles.modalBtns}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={onClose}><Text style={styles.modalCancelText}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={[styles.modalConfirmBtn, { backgroundColor: colors.sunflower }]} onPress={onGoTraining}>
                <Ionicons name="school" size={17} color={colors.black} style={{ marginRight: 6 }} />
                <Text style={[styles.modalConfirmText, { color: colors.black }]}>Go to Training</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <TouchableOpacity style={styles.modalBackdrop} onPress={onClose} activeOpacity={1} />
        <View style={styles.modalSheet}>
          <View style={styles.modalHandle} />
          <Text style={styles.modalTitle}>Confirm Job</Text>
          <Text style={styles.modalSub}>{title}</Text>
          <Text style={styles.modalPayout}>${Math.round(job.payoutAmount || 0)}</Text>

          <View style={styles.modalAddrBox}>
            <View style={styles.modalAddrRow}>
              <Ionicons name="arrow-up-circle" size={14} color={colors.gray50} />
              <Text style={styles.modalAddrText} numberOfLines={1}>{job.pickupAddress || 'Pickup address pending'}</Text>
            </View>
            <View style={styles.modalAddrRow}>
              <Ionicons name="arrow-down-circle" size={14} color={colors.black} />
              <Text style={styles.modalAddrText} numberOfLines={1}>{job.deliveryAddress || 'Address pending'}</Text>
            </View>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 4 }}>
            <Ionicons name="calendar" size={18} color={colors.black} />
            <Text style={[styles.windowLabel, { marginTop: 0, marginBottom: 0, fontSize: 16 }]}>Select Pickup Window</Text>
          </View>
          {(() => {
            const paired = pairPickupWithDelivery(job.pickupWindows, job.deliveryWindows, job.scheduledWindowStart);
            if (paired.length === 0) {
              return (
                <View style={{ borderRadius: 10, padding: 14, backgroundColor: '#FFF9E6', borderWidth: 1, borderColor: '#EEB437', marginBottom: 8 }}>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: colors.gray85, marginBottom: 4 }}>Pickup time pending</Text>
                  <Text style={{ fontSize: 12, color: colors.gray70, lineHeight: 17 }}>
                    The seller hasn't shared pickup availability yet. You'll be able to claim this job once they do.
                  </Text>
                </View>
              );
            }
            const tzOpts: Intl.DateTimeFormatOptions = job.pickupTimezone ? { timeZone: job.pickupTimezone } : {};
            return (
              <>
                <Text style={{ fontSize: 12, color: colors.gray50, marginBottom: 10 }}>You must select a pickup window to accept this job</Text>
                {paired.map((p, idx) => {
                  const bp = bufferedPickup(p.pickupStart, p.pickupEnd);
                  const day = bp.start!.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...tzOpts });
                  const time = `${bp.start!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}${bp.end ? ` – ${bp.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...tzOpts })}` : ''}`;
                  const selected = selectedWindow === p.deliveryWindowId;
                  return (
                    <TouchableOpacity key={p.deliveryWindowId} style={[styles.windowOpt, selected && styles.windowSel]} onPress={() => setSelectedWindow(p.deliveryWindowId)}>
                      <View style={[styles.radio, selected && styles.radioSel]}>
                        {selected && <View style={styles.radioDot} />}
                      </View>
                      <View>
                        <Text style={[styles.windowDay, selected && { color: colors.black }]}>{day}</Text>
                        <Text style={styles.windowTime}>{time}</Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </>
            );
          })()}

          <View style={styles.modalBtns}>
            <TouchableOpacity style={styles.modalCancelBtn} onPress={onClose}><Text style={styles.modalCancelText}>Cancel</Text></TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalConfirmBtn, !selectedWindow && styles.disabled]}
              onPress={() => selectedWindow && onAccept(job, selectedWindow)}
              disabled={!selectedWindow || loading}
            >
              {loading ? <ActivityIndicator color={colors.white} /> : <Text style={styles.modalConfirmText}>Accept & Schedule</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* ── Reject Modal ── */
function RejectModal({ job, visible, onClose, onReject }: any) {
  const [reason, setReason] = useState<string | null>(null);
  if (!job) return null;
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <TouchableOpacity style={styles.modalBackdrop} onPress={onClose} activeOpacity={1} />
        <View style={styles.modalSheet}>
          <View style={styles.modalHandle} />
          <Text style={styles.modalTitle}>Pass on this job?</Text>
          <Text style={styles.modalSub}>{getJobDisplayTitle(job)}</Text>
          <Text style={styles.windowLabel}>Why are you passing?</Text>
          {REJECT_REASONS.map(r => (
            <TouchableOpacity key={r.id} style={[styles.windowOpt, reason === r.id && styles.windowSel]} onPress={() => setReason(r.id)}>
              <View style={[styles.radio, reason === r.id && styles.radioSel]}>
                {reason === r.id && <View style={styles.radioDot} />}
              </View>
              <Text style={[styles.windowDay, reason === r.id && { color: colors.black }]}>{r.label}</Text>
            </TouchableOpacity>
          ))}
          <View style={styles.modalBtns}>
            <TouchableOpacity style={styles.modalCancelBtn} onPress={onClose}><Text style={styles.modalCancelText}>Cancel</Text></TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalConfirmBtn, { backgroundColor: colors.gray70 }, !reason && styles.disabled]}
              onPress={() => reason && onReject(job.id, reason)}
              disabled={!reason}
            ><Text style={styles.modalConfirmText}>Pass</Text></TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* ── Styles ── */
const styles = StyleSheet.create({
  container:      { flex: 1, backgroundColor: RD.bg },

  // Header
  header:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 60 : 40, paddingBottom: 6 },
  headerTitle:    { fontSize: 28, fontWeight: '800', color: RD.text, fontFamily: fonts.heavy, letterSpacing: -0.5 },
  headerSub:      { fontSize: 13, color: RD.textFaint, marginTop: 2, letterSpacing: 0.2 },
  countBubble:    { backgroundColor: RD.ink, borderRadius: radii.full, width: 32, height: 32, justifyContent: 'center', alignItems: 'center', marginTop: 4 },
  countText:      { color: '#fff', fontSize: 14, fontWeight: '800' },

  // Refresh
  refreshRow:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 6 },
  refreshText:    { fontSize: 12, color: RD.textFaint, fontWeight: '500' },

  // Filter
  filterRow:      { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: spacing.lg, gap: 6, paddingBottom: 8 },

  // Pills / filter chips
  pill:           { flexDirection: 'row', alignItems: 'center', gap: 4, height: 32, paddingHorizontal: 12, borderRadius: 999, backgroundColor: RD.g05, borderWidth: 1, borderColor: RD.border },
  pillActive:     { backgroundColor: RD.ink, borderColor: RD.ink },
  pillText:       { fontSize: 13, fontWeight: '600', color: RD.textMuted },
  pillTextActive: { color: '#fff' },
  pillBadge:      { marginLeft: 2, backgroundColor: RD.g10, borderRadius: 999, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 4 },
  pillBadgeActive:{ backgroundColor: 'rgba(255,255,255,0.22)' },
  pillBadgeText:  { fontSize: 10, fontWeight: '700', color: RD.textMuted },
  pillBadgeTextActive: { color: '#fff' },

  // List
  list:           { padding: spacing.base, gap: 14, paddingBottom: Platform.OS === 'ios' ? 110 : 88 },

  // Empty
  emptyState:     { alignItems: 'center', paddingTop: 60, gap: 12 },
  emptyTitle:     { fontSize: 18, fontWeight: '700', color: RD.textSec },
  emptyText:      { fontSize: 14, color: RD.textFaint, textAlign: 'center', paddingHorizontal: 40, lineHeight: 20 },

  // Card
  card:           { backgroundColor: RD.surface, borderRadius: 18, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1, overflow: 'hidden', borderWidth: 1, borderColor: RD.border },

  // Card top — title/route + pills on the left, payout on the right
  cardTop:        { flexDirection: 'row', justifyContent: 'space-between', gap: 12 },
  cardTopLeft:    { flex: 1 },
  orderTitle:     { fontSize: 15.5, fontWeight: '700', color: RD.text, letterSpacing: -0.2 },
  routeText:      { fontSize: 13, color: RD.textMuted, marginTop: 2 },

  // Payout
  payoutBlock:    { alignItems: 'flex-end', flexShrink: 0 },
  payout:         { fontSize: 22, fontWeight: '700', color: RD.money, fontFamily: fonts.heavy, letterSpacing: -0.5 },
  payoutLabel:    { fontSize: 11, color: RD.g40, marginTop: 1 },

  // Soft pills
  pillsRow:       { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 10 },
  softPill:       { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: RD.g05, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 },
  softPillText:   { fontSize: 11.5, color: RD.textMuted, fontWeight: '600' },
  softPillMaroon: { backgroundColor: RD.maroonSoft },
  softPillAmber:  { backgroundColor: RD.amberSoft },
  windowsPreview:     { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: RD.border, gap: 5 },
  windowsPreviewLabel:{ fontSize: 11, fontWeight: '700', color: RD.textFaint, letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 2 },
  windowsPreviewRow:  { flexDirection: 'row', alignItems: 'center', gap: 6 },
  windowsPreviewText: { fontSize: 13, color: RD.text, fontWeight: '600' },
  windowsPreviewMore: { fontSize: 12, color: RD.maroon, fontWeight: '600', marginTop: 1 },

  // Action row
  actionRow:      { flexDirection: 'row', gap: 9, marginTop: 14 },
  passBtn:        { flex: 1, height: 54, borderRadius: 16, borderWidth: 1.5, borderColor: RD.borderStrong, backgroundColor: RD.surface, justifyContent: 'center', alignItems: 'center' },
  passText:       { color: RD.textSec, fontWeight: '600', fontSize: 16 },
  acceptBtn:      { flex: 2.4, height: 54, borderRadius: 16, backgroundColor: RD.ink, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 6, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  acceptText:     { color: '#fff', fontWeight: '700', fontSize: 16, letterSpacing: 0.2 },
  lockedBtn:      { flex: 2.4, height: 54, borderRadius: 16, borderWidth: 1.5, borderColor: RD.borderStrong, backgroundColor: RD.surface, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 6 },
  lockedBtnText:  { color: RD.textSec, fontWeight: '700', fontSize: 16 },

  // Modal shared
  modalOverlay:   { flex: 1, justifyContent: 'flex-end' },
  modalBackdrop:  { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' },
  modalSheet:     { backgroundColor: RD.surface, borderTopLeftRadius: radii.xxl, borderTopRightRadius: radii.xxl, padding: spacing.xl, paddingBottom: 40 },
  modalHandle:    { width: 36, height: 4, borderRadius: 2, backgroundColor: RD.g15, alignSelf: 'center', marginBottom: spacing.lg },
  modalTitle:     { fontSize: 22, fontWeight: '800', color: RD.text, marginBottom: 4, textAlign: 'center' },
  modalSub:       { fontSize: 14, color: RD.textSec, marginBottom: 4, textAlign: 'center' },
  modalPayout:    { fontSize: 28, fontWeight: '700', color: RD.money, marginBottom: spacing.sm, textAlign: 'center' },
  modalAddrBox:   { backgroundColor: RD.g03, borderRadius: radii.md, padding: 10, marginBottom: spacing.lg, gap: 6, borderWidth: 1, borderColor: RD.border },
  modalAddrRow:   { flexDirection: 'row', alignItems: 'center', gap: 8 },
  modalAddrText:  { fontSize: 13, color: RD.textSec, flex: 1 },
  windowLabel:    { fontSize: 14, fontWeight: '700', color: RD.text, marginBottom: spacing.md },
  windowOpt:      { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: radii.md, borderWidth: 1.5, borderColor: RD.border, marginBottom: 8 },
  windowSel:      { borderColor: RD.ink, backgroundColor: RD.g03 },
  radio:          { width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: RD.g15, justifyContent: 'center', alignItems: 'center' },
  radioSel:       { borderColor: RD.ink },
  radioDot:       { width: 9, height: 9, borderRadius: 5, backgroundColor: RD.ink },
  windowDay:      { fontSize: 14, fontWeight: '600', color: RD.text },
  windowTime:     { fontSize: 12, color: RD.textFaint, marginTop: 1 },
  modalBtns:      { flexDirection: 'row', gap: 12, marginTop: spacing.lg },
  modalCancelBtn: { flex: 1, height: 54, borderRadius: 16, borderWidth: 1.5, borderColor: RD.borderStrong, backgroundColor: RD.surface, justifyContent: 'center', alignItems: 'center' },
  modalCancelText:{ color: RD.textSec, fontWeight: '600', fontSize: 16 },
  modalConfirmBtn:{ flex: 2, height: 54, borderRadius: 16, backgroundColor: RD.ink, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' },
  modalConfirmText:{ color: '#fff', fontWeight: '600', fontSize: 16 },
  disabled:       { opacity: 0.4 },

  // Locked modal
  lockedContent:  { alignItems: 'center', paddingVertical: spacing.lg },
  lockedIcon:     { width: 60, height: 60, borderRadius: 30, backgroundColor: RD.amberSoft, justifyContent: 'center', alignItems: 'center', marginBottom: spacing.sm },
  lockedDesc:     { fontSize: 15, color: RD.textSec, textAlign: 'center', lineHeight: 22, marginTop: 6, paddingHorizontal: spacing.base },
  lockedPayout:   { fontSize: 14, fontWeight: '600', color: RD.money, marginTop: spacing.md },
});
